import React, { useState, useEffect, useCallback } from 'react'
import TrackMap, { CarOnTrack } from './TrackMap'
import DriverAvatar from './DriverAvatar'
import { useRaceSimulation } from '../hooks/useRaceSimulation'

const PLAYER_COLORS = [
  '#e53e3e','#3182ce','#38a169','#d69e2e',
  '#805ad5','#dd6b20','#319795','#e53e7a',
  '#4299e1','#48bb78','#ecc94b',
]

interface Assignment { playerName: string; drivers: { number: number; name: string; team?: string }[] }
interface Prizes      { first: number; second: number; third: number }
interface LiveEntry   { position: number; driverNumber: number; driverName: string; speed: number; location: string; isDNF: boolean }
interface LeaderboardData { entries: LiveEntry[]; raceStatus: string; timestamp: string; source: string; raceDay?: boolean }

interface Props {
  playerIndex:  number
  assignments:  Assignment[]
  prizes:       Prizes
  onBack:       () => void
}

const MEDALS = ['🥇','🥈','🥉']
const POS_COLORS = ['#d4a017','#9ca3af','#cd7f32']

export default function PlayerPage({ playerIndex, assignments, prizes, onBack }: Props) {
  const player     = assignments[playerIndex]
  const color      = PLAYER_COLORS[playerIndex % PLAYER_COLORS.length]
  const myNumbers  = new Set(player.drivers.map(d => d.number))

  type FilterMode = 'mine' | 'others' | 'top3' | 'dnf'
  const [liveData,        setLiveData]        = useState<LeaderboardData | null>(null)
  const [simMode,         setSimMode]         = useState(false)
  const [filterMode,      setFilterMode]      = useState<FilterMode>('mine')
  const [selectedDrivers, setSelectedDrivers] = useState<Set<number>>(new Set())
  const [lastUpdated,     setLastUpdated]     = useState('')
  const [error,           setError]           = useState('')

  const toggleDriver = (num: number) =>
    setSelectedDrivers(prev => {
      const next = new Set(prev)
      if (next.has(num)) next.delete(num); else next.add(num)
      return next
    })

  // ── Simulation ────────────────────────────────────────────────────────
  const { entries: simEntries, lap: simLap } = useRaceSimulation(simMode)

  // ── Live polling ──────────────────────────────────────────────────────
  const fetchLive = useCallback(async () => {
    try {
      const res  = await fetch('/api/race/live')
      const data = await res.json()
      setLiveData(data)
      setLastUpdated(new Date().toLocaleTimeString())
      setError('')
    } catch {
      setError('Could not reach race data.')
    }
  }, [])

  useEffect(() => {
    if (simMode) return
    fetchLive()
    const id = setInterval(fetchLive, 10_000)
    return () => clearInterval(id)
  }, [fetchLive, simMode])

  // ── Derive display entries ────────────────────────────────────────────
  const allEntries: LiveEntry[] = simMode
    ? simEntries
    : (liveData?.entries ?? [])

  const myEntries = allEntries
    .filter(e => myNumbers.has(e.driverNumber))
    .sort((a, b) => a.position - b.position)

  // ── Payout ───────────────────────────────────────────────────────────
  let payout = 0
  myEntries.forEach(e => {
    if (e.position === 1) payout += prizes.first
    if (e.position === 2) payout += prizes.second
    if (e.position === 3) payout += prizes.third
  })

  const dnfCount   = myEntries.filter(e => e.isDNF).length
  const bestPos    = myEntries.find(e => !e.isDNF)?.position ?? null
  const isMock     = !simMode && liveData?.source === 'mock'
  const isRaceDay  = liveData?.raceDay === true
  const currentLap = simMode ? simLap : null

  // ── Build track cars ─────────────────────────────────────────────────
  const trackCars: CarOnTrack[] = allEntries.map(e => ({
    driverNumber:  e.driverNumber,
    driverName:    e.driverName,
    location:      e.location,
    position:      e.position,
    isDNF:         e.isDNF,
    isHighlighted: myNumbers.has(e.driverNumber),
    color,
  }))

  // ── All cars with per-player colors ──────────────────────────────────────
  const allTrackCars: CarOnTrack[] = allEntries.map(e => {
    const ownerIdx = assignments.findIndex(a => a.drivers.some(d => d.number === e.driverNumber))
    return {
      driverNumber:  e.driverNumber,
      driverName:    e.driverName,
      location:      e.location,
      position:      e.position,
      isDNF:         e.isDNF,
      isHighlighted: ownerIdx === playerIndex,
      color: ownerIdx >= 0 ? PLAYER_COLORS[ownerIdx % PLAYER_COLORS.length] : '#ffffff',
    }
  })

  // ── Apply filter / selection ──────────────────────────────────────────────
  // Driver selection (from right panel) overrides filter: show full field,
  // only pinned drivers highlighted.
  const visibleCars: CarOnTrack[] = (() => {
    if (selectedDrivers.size > 0)
      return allTrackCars.map(c => ({ ...c, isHighlighted: selectedDrivers.has(c.driverNumber) }))
    if (filterMode === 'mine')
      return allTrackCars.filter(c => c.isHighlighted)
    if (filterMode === 'others')
      return allTrackCars.filter(c => !c.isHighlighted).map(c => ({ ...c, isHighlighted: true }))
    if (filterMode === 'top3')
      return allTrackCars.filter(c => c.position <= 3 && !c.isDNF).map(c => ({ ...c, isHighlighted: true }))
    return allTrackCars.filter(c => c.isDNF).map(c => ({ ...c, isHighlighted: true }))
  })()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* ── Header ── */}
      <div className="race-header" style={{ borderBottom: `3px solid ${color}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="nav-btn" onClick={onBack} style={{ fontSize: '0.85rem' }}>
            ← All Players
          </button>
          <div style={{ color, fontWeight: 900, fontSize: '1.4rem',
            fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: 2 }}>
            {player.playerName}
          </div>
          {bestPos && bestPos <= 3 && (
            <span style={{ fontSize: '1.5rem' }}>{MEDALS[bestPos - 1]}</span>
          )}
          {bestPos && (
            <span style={{ fontSize: '0.85rem', color: bestPos <= 3 ? POS_COLORS[bestPos - 1] : 'rgba(255,255,255,0.5)' }}>
              Best: P{bestPos}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
          {payout > 0 && (
            <span style={{ color: '#d4a017', fontWeight: 800, fontSize: '1rem' }}>
              💰 +${payout}
            </span>
          )}
          {dnfCount > 0 && (
            <span className="race-dnf-badge">{dnfCount} DNF</span>
          )}
          {simMode && (
            <span style={{ background: '#805ad5', color: '#fff', borderRadius: 6,
              padding: '0.2rem 0.7rem', fontSize: '0.78rem', fontWeight: 700 }}>
              🎬 SIM · Lap {currentLap ?? '…'}/200
            </span>
          )}
          {!simMode && isMock && isRaceDay && (
            <span className="race-status-badge status-raceday">📡 Live Data</span>
          )}
          {!simMode && isMock && !isRaceDay && (
            <span className="race-status-badge status-mock">🎮 Demo Mode</span>
          )}
          {!simMode && !isMock && liveData && (
            <span className="race-status-badge status-live">🔴 LIVE</span>
          )}
          {!simMode && lastUpdated && (
            <span className="last-updated">Updated {lastUpdated}</span>
          )}
          <button
            className="btn"
            onClick={() => setSimMode(m => !m)}
            style={{ background: simMode ? '#805ad5' : 'rgba(128,90,213,0.2)',
              color: '#fff', border: '1px solid #805ad5',
              padding: '0.35rem 0.9rem', fontSize: '0.8rem', borderRadius: 6 }}
          >
            {simMode ? '⏹ Stop Simulation' : '▶ Simulate Race'}
          </button>
        </div>
      </div>

      {error && !simMode && <div className="alert alert-error">{error}</div>}

      {/* ── Main layout: track left, driver panel right ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.2rem', alignItems: 'start' }}>

        {/* ── Track map ── */}
        <div className="card" style={{ padding: '0.8rem', borderTop: `3px solid ${color}` }}>
          {/* Filter toggle */}
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.6rem', justifyContent: 'center' }}>
            {([
              { key: 'mine',   label: `${player.playerName}'s Drivers` },
              { key: 'others', label: 'Other Drivers' },
              { key: 'top3',   label: '🥇 Top 3' },
              { key: 'dnf',    label: 'DNF' },
            ] as { key: typeof filterMode; label: string }[]).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => { setFilterMode(key); setSelectedDrivers(new Set()) }}
                style={{
                  padding: '0.25rem 0.7rem', fontSize: '0.72rem', borderRadius: 5, cursor: 'pointer',
                  fontFamily: 'Montserrat,sans-serif', fontWeight: filterMode === key ? 700 : 400,
                  background: filterMode === key
                    ? key === 'dnf' ? '#c81026' : color
                    : 'rgba(255,255,255,0.07)',
                  color: '#fff',
                  border: `1px solid ${filterMode === key ? 'transparent' : 'rgba(255,255,255,0.15)'}`,
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)',
            marginBottom: '0.4rem', textAlign: 'center', letterSpacing: 1 }}>
            INDIANAPOLIS MOTOR SPEEDWAY — {allEntries.length} CARS ON TRACK
          </div>
          {allEntries.length === 0 ? (
            <div className="loading-spinner" style={{ margin: '3rem auto' }}/>
          ) : visibleCars.length === 0 && filterMode === 'dnf' ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem',
              color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem' }}>
              No DNFs yet
            </div>
          ) : (
            <TrackMap cars={visibleCars} />
          )}
          {/* Legend */}
          <div style={{ display: 'flex', gap: '1.2rem', justifyContent: 'center',
            marginTop: '0.7rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, border: '1.5px solid white' }}/>
              {player.playerName}'s drivers
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }}/>
              Other drivers
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(200,16,38,0.4)' }}/>
              DNF
            </div>
          </div>
        </div>

        {/* ── Driver data panel ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>

          {/* Payout summary */}
          <div className="card" style={{ padding: '0.8rem 1rem', borderTop: `3px solid ${color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', letterSpacing: 1 }}>
                CURRENT PAYOUT
              </span>
              <span style={{ fontSize: '1.5rem', fontWeight: 900, color: payout > 0 ? '#d4a017' : 'rgba(255,255,255,0.2)',
                fontFamily: 'Montserrat,sans-serif' }}>
                ${payout}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {[
                { label: '🥇', val: prizes.first },
                { label: '🥈', val: prizes.second },
                { label: '🥉', val: prizes.third },
              ].map(({ label, val }) => (
                <span key={label} style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)',
                  background: 'rgba(255,255,255,0.05)', borderRadius: 4, padding: '0.15rem 0.5rem' }}>
                  {label} ${val}
                </span>
              ))}
            </div>
          </div>

          {/* Per-driver cards */}
          {myEntries.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '0 0.2rem', marginBottom: '-0.3rem' }}>
              <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)', letterSpacing: 0.5 }}>
                {selectedDrivers.size > 0
                  ? `${selectedDrivers.size} pinned on track`
                  : 'Click driver to pin on track'}
              </span>
              {selectedDrivers.size > 0 && (
                <button
                  onClick={() => setSelectedDrivers(new Set())}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
                    fontSize: '0.68rem', cursor: 'pointer', padding: '0.1rem 0.3rem',
                    borderRadius: 4, fontFamily: 'Montserrat,sans-serif' }}
                >
                  × Clear
                </button>
              )}
            </div>
          )}
          {myEntries.length === 0 ? (
            <div className="card" style={{ padding: '1.5rem', textAlign: 'center',
              color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
              Loading driver data…
            </div>
          ) : (
            myEntries.map(e => {
              const driver   = player.drivers.find(d => d.number === e.driverNumber)
              const isTop3   = e.position <= 3 && !e.isDNF
              const isPinned = selectedDrivers.has(e.driverNumber)
              const accentColor = e.isDNF ? '#c81026' : isTop3 ? POS_COLORS[e.position - 1] : color
              return (
                <div
                  key={e.driverNumber}
                  onClick={() => toggleDriver(e.driverNumber)}
                  className="card"
                  style={{
                    padding: '0.75rem 1rem',
                    borderLeft: `4px solid ${accentColor}`,
                    opacity: e.isDNF ? 0.65 : 1,
                    cursor: 'pointer',
                    outline: isPinned ? `2px solid ${color}` : '2px solid transparent',
                    outlineOffset: '-1px',
                    boxShadow: isPinned ? `0 0 14px ${color}55, inset 0 0 0 1px ${color}33` : undefined,
                    background: isPinned
                      ? `${color}1f`
                      : isTop3
                        ? `rgba(${e.position === 1 ? '212,160,23' : e.position === 2 ? '156,163,175' : '205,127,50'},0.07)`
                        : undefined,
                    transition: 'box-shadow 0.15s, outline-color 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    {/* Position badge */}
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                      background: e.isDNF ? '#c81026' : isTop3 ? POS_COLORS[e.position - 1] : 'rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: e.position <= 9 ? '1rem' : '0.8rem', fontWeight: 900, color: '#fff',
                    }}>
                      {e.isDNF ? '✕' : isTop3 ? MEDALS[e.position - 1] : `P${e.position}`}
                    </div>

                    <DriverAvatar name={e.driverName} number={e.driverNumber} color={color} size={36}/>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', display: 'flex',
                        alignItems: 'center', gap: '0.35rem' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {e.driverName}
                        </span>
                        {isPinned && (
                          <span title="Pinned on track" style={{ fontSize: '0.72rem', color, flexShrink: 0 }}>📍</span>
                        )}
                      </div>
                      {driver?.team && (
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {driver.team}
                        </div>
                      )}
                    </div>

                    {/* Speed / DNF */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {e.isDNF ? (
                        <span style={{ background: '#c81026', color: '#fff', borderRadius: 4,
                          padding: '0.2rem 0.5rem', fontSize: '0.72rem', fontWeight: 700 }}>
                          DNF
                        </span>
                      ) : e.speed > 0 ? (
                        <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)',
                          fontFamily: 'Montserrat,sans-serif' }}>
                          {e.speed.toFixed(1)}<br/>
                          <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)' }}>mph</span>
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {/* Location bar — only show when we have a meaningful location */}
                  {!e.isDNF && (e.location && e.location !== 'On Track' || e.position <= 3) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                      marginTop: '0.5rem', paddingTop: '0.5rem',
                      borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }}/>
                      <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', flex: 1 }}>
                        {e.location && e.location !== 'On Track' ? e.location : ''}
                      </span>
                      {(e.position === 1 || e.position === 2 || e.position === 3) && (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700,
                          color: POS_COLORS[e.position - 1] }}>
                          +${[prizes.first, prizes.second, prizes.third][e.position - 1]}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}

          {/* DNF section at bottom */}
          {dnfCount > 0 && (
            <div style={{ fontSize: '0.72rem', color: 'rgba(200,16,38,0.7)',
              textAlign: 'center', padding: '0.3rem',
              border: '1px solid rgba(200,16,38,0.2)', borderRadius: 6 }}>
              {dnfCount} driver{dnfCount > 1 ? 's' : ''} out of race
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
